import React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import {
  passwordComplexityValidation,
  samePasswordCheck,
  useValidators,
} from "../../../helpers/validation";
import { handleFormServerErrors } from "../../../helpers/errorHandling";
import { FormSubmitHandler } from "../../../common/models";
import TextInput from "../TextInput";
import FormError from "../FormError";
import SubmitButton from "../SubmitButton";
import { showToast } from "../../../helpers/toastHelpers";

interface Props {
  onSubmit: FormSubmitHandler<ChangePasswordFormValues>;
}

export interface ChangePasswordFormValues {
  oldPassword: string;
  newPassword: string;
  newPasswordAgain: string;
}

const ChangePasswordForm: React.FC<Props> = (props): React.ReactElement => {
  const { tInput, tError, t } = useCustomTranslation();
  const validators = useValidators();

  const methods = useForm<ChangePasswordFormValues>({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      newPasswordAgain: "",
    },
    mode: "onBlur",
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<ChangePasswordFormValues> = async (data) => {
    try {
      await props.onSubmit(data);
      showToast(t("password_change_success"), "success");

      reset();
    } catch (e) {
      handleFormServerErrors(e, tError, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <form className="sm:w-[480px]" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          className="mb-2"
          name="oldPassword"
          placeholder={tInput("password_old_input")}
          type="password"
          required
          options={{
            required: validators.required,
          }}
        />
        <TextInput
          name="newPassword"
          placeholder={tInput("password_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
            },
          }}
        />
        <TextInput
          name="newPasswordAgain"
          placeholder={tInput("password_again_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
              samePasswordCheck: (value, values) =>
                samePasswordCheck(
                  value as string,
                  values.newPassword as string,
                ),
            },
          }}
        />
        <p className="text-gray-dark mt-2 mb-4 text-[14px]/[24px] tracking-[-0.14px]">
          {tInput("password_complex_required")}
        </p>
        <FormError />
        <SubmitButton
          label={tInput("confirm_password_change_button")}
          size="small"
        />
      </form>
    </FormProvider>
  );
};

export default ChangePasswordForm;
